import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { newBlob } from "@/store/audio-capture/audio-capture-error-handling-helpers";
import { getAudioDurationSeconds } from "@/store/audio-capture/audio/audio-duration";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
import {
  isDesktopPcmBlob,
  DESKTOP_PCM_SAMPLE_RATE,
} from "@/store/audio-capture/desktop/capture/desktop-pcm-persistence";
import { DesktopAudioAssembler } from "@/store/audio-capture/desktop/capture/desktop-audio-assembler";
import type { PersistedAudioCaptureState } from "@/store/audio-capture/persistence/audio-capture-persistence";

export class AudioCaptureRestorer {
  constructor(private readonly store: AppStoreAudioCaptureStore) {}

  async restore({ signal }: { signal: AbortSignal }) {
    logger.info({
      message: "[AppStoreAudioCaptureStore] initializeFromDb - Start",
    });

    let loadFailed = false;
    let state: PersistedAudioCaptureState | undefined;
    try {
      state = await this.store.persistence.getLatestState();
    } catch (err) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] initializeFromDb",
        info: { error: objectModule.safeErrorAsJson(err as Error) },
      });
      loadFailed = true;
    }

    if (loadFailed || signal.aborted) {
      logger.warn({
        message:
          "[AppStoreAudioCaptureStore] initializeFromDb - Aborted after getAudioCaptureState",
        info: {
          loadFailed,
          signalAborted: signal.aborted,
        },
      });
      return;
    }

    if (!state) return;

    this.store.restoreRecordingSessionFromState(state);

    try {
      // If note is gone, don't lock audio capture.
      const note = await this.store.store.notes.getAsync(state.noteId);
      if (!note || note.isDeleted) return;
    } catch (err) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] initializeFromDb",
        info: { error: objectModule.safeErrorAsJson(err as Error) },
      });
      return;
    }

    const {
      noteId,
      capturedAudioId,
      audioBlobs: audioChunks,
      transcripts,
      transcriptSnapshots,
    } = state;

    const restoredDuration = await (async (): Promise<number> => {
      if (audioChunks.length === 0 || signal.aborted) {
        logger.warn({
          message: "[AppStoreAudioCaptureStore] initializeFromDb [getAudioDuration]",
          info: { audioChunks: audioChunks.length },
        });

        return 0;
      }
      try {
        const hasDesktopPcm = audioChunks.some(isDesktopPcmBlob);
        if (hasDesktopPcm) {
          const dataBytes = audioChunks
            .filter(isDesktopPcmBlob)
            .reduce((acc, b) => acc + b.size, 0);
          return dataBytes / 2 / DESKTOP_PCM_SAMPLE_RATE;
        }
        logger.info({
          message: "[AppStoreAudioCaptureStore] initializeFromDb [getAudioDuration]",
          info: { audioChunks: audioChunks.length },
        });
        const blob = newBlob(audioChunks, { type: audioChunks[0].type });
        return blob ? getAudioDurationSeconds(blob) : 0;
      } catch (error) {
        logger.error({
          message: "[AppStoreAudioCaptureStore] initializeFromDb [getAudioDuration]",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
        return 0;
      }
    })();

    if (signal.aborted) {
      logger.warn({
        message: "[AppStoreAudioCaptureStore] initializeFromDb - Aborted after restoredDuration",
        info: {
          audioChunks: audioChunks.length,
          audioChunksType: audioChunks[0].type,
        },
      });
      return;
    }

    logger.info({
      message: "[AppStoreAudioCaptureStore] initializeFromDb [newBlob]",
      info: { audioChunks: audioChunks.length },
    });

    let audioBlob: Blob | null | undefined;
    const hasDesktopPcm = audioChunks.some(isDesktopPcmBlob);
    if (hasDesktopPcm) {
      try {
        audioBlob = await DesktopAudioAssembler.createWavFromPcmBlobs(
          audioChunks.filter(isDesktopPcmBlob),
          DESKTOP_PCM_SAMPLE_RATE
        );
      } catch (err) {
        logger.error({
          message: "[AppStoreAudioCaptureStore] initializeFromDb [createWavFromPcmBlobs]",
          info: { error: objectModule.safeErrorAsJson(err as Error) },
        });
      }
    } else {
      audioBlob = newBlob(audioChunks, {
        type: audioChunks[0]?.type ?? "audio/webm",
      });
    }

    logger.info({
      message: "[AppStoreAudioCaptureStore] initializeFromDb",
      info: {
        noteId,
        capturedAudioId,
        audioChunks: audioChunks.length,
        audioBlobSize: audioBlob?.size ?? null,
        transcripts: transcripts?.map(e => e.length),
        snapshots: transcriptSnapshots?.map(e => e.length),
        restoredDuration,
      },
    });

    // If the audio duration is 0 or blob is malformed, it means the audio file is invalid.
    if (restoredDuration === 0 || !audioBlob) return;

    try {
      const audioUrl = URL.createObjectURL(audioBlob);

      logger.info({
        message: "[AppStoreAudioCaptureStore] initializeFromDb [URL.createObjectURL]",
        info: { length: audioChunks.length },
      });

      this.store._withAudioCaptureLock(
        async () => {
          this.store.setStatus(RecordingStatus.LOCKED);
          this.store.capturedAudioId = capturedAudioId;
          this.store.activeNoteId = noteId;
          this.store.audioBlob = audioBlob;
          this.store.audioUrl = audioUrl;
          this.store.transcriptions[0]?.restoreTranscripts?.({
            transcriptSnapshots: transcriptSnapshots[0],
            transcripts: transcripts[0],
          });

          this.store.audioChunks = audioChunks || [];
          this.store.restoredDuration = restoredDuration;
        },
        { showErrorToast: false }
      );
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] Failed to restore recording",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }
}
